import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { SmartImage } from "../ui/SmartImage";

const MAX_SLIDES = 4;
const INTERVAL_MS = 3200;

export const CardImageCarousel = ({ images, name, interval = INTERVAL_MS }) => {
  const slides = (images ?? []).slice(0, MAX_SLIDES);
  const [ratio, setRatio] = useState(4 / 5);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [visible, setVisible] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    const node = rootRef.current;
    if (!node) return;
    const io = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { rootMargin: "80px" });
    io.observe(node);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const first = slides[0];
    if (!first || !visible) return;
    const img = new window.Image();
    img.onload = () => {
      if (img.naturalWidth && img.naturalHeight) setRatio(img.naturalWidth / img.naturalHeight);
    };
    img.src = first;
  }, [slides, visible]);

  useEffect(() => {
    if (!visible || paused || slides.length < 2) return;
    const timer = setInterval(() => setActive((a) => (a + 1) % slides.length), interval);
    return () => clearInterval(timer);
  }, [visible, paused, slides.length, interval]);

  if (slides.length === 0) return null;

  return (
    <div
      ref={rootRef}
      data-testid="card-image-carousel"
      className="relative w-full overflow-hidden"
      style={{ aspectRatio: ratio }}
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <AnimatePresence initial={false}>
        <motion.div
          key={active}
          className="absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <SmartImage
            src={slides[active]}
            alt={`${name} — view ${active + 1}`}
            label={name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-[1100ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
          />
        </motion.div>
      </AnimatePresence>

      <div className="absolute bottom-2.5 inset-x-0 flex items-center justify-center gap-1.5">
        {slides.length > 1 &&
          slides.map((s, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              aria-label={`Show view ${i + 1}`}
              aria-pressed={i === active}
              data-testid={`card-dot-${i}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === active ? "w-4 bg-charcoal" : "w-1.5 bg-charcoal/40 hover:bg-charcoal/70"
              }`}
            />
          ))}
      </div>
    </div>
  );
};

export default CardImageCarousel;